import {
  attachmentId,
  pixelBufferType,
  primitiveType,
  stencilFunc,
  stencilValue
} from "_src/engine/constants";

import Pass from "_src/render_graph/pass";
import FrameBuffer from "_src/engine/framebuffer";
import Camera from "_src/engine/camera";
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import resourceManager from "_src/render_graph/resource_manager";
import ScreenShader from "_src/shaders/screen/screen";
import {screenUtils} from "_src/client/utils";
import OutlineShader from "_src/shaders/outline/outline";

const BORDER_COLOR = [1, 1, 1];
const BORDER_DEFAULT_TRANSPARENCY = 0.6;
const BORDER_SELECTED_TRANSPARENCY = 1.0;
const BORDER_DEFAULT_LINE_WIDTH = 1;
const BORDER_ACTIVE_LINE_WIDTH = 2;

export default class PhotosPass extends Pass {

  constructor(attachmentsStorage, camera) {

    super(attachmentsStorage);

    this.init(camera);

  }

  init(camera) {
    const screenCamera = new Camera();

    const drawScene = this._addScene(camera);
    const defaultMaskScene = this._addScene(screenCamera);
    const hoveredMaskScene = this._addScene(screenCamera);
    const activeMaskScene = this._addScene(screenCamera);
    const outlineScene = this._addScene(screenCamera);

    drawScene.clearedColor = false;
    drawScene.clearedDepth = false;
    drawScene.clearedStencil = false;

    defaultMaskScene.clearedColor = true;
    defaultMaskScene.clearedStencil = false;
    defaultMaskScene.clearedDepth = false;

    hoveredMaskScene.clearedColor = true;
    hoveredMaskScene.clearedStencil = false;
    hoveredMaskScene.clearedDepth = false;

    activeMaskScene.clearedColor = true;
    activeMaskScene.clearedStencil = false;
    activeMaskScene.clearedDepth = false;

    outlineScene.clearedColor = false;
    outlineScene.clearedDepth = false;
    outlineScene.clearedStencil = false;

    const screenChunk = primitiveFactory.create(primitiveType.SCREEN_QUAD);

    const defaultMaskShader = new ScreenShader({transparent : true, depthTest: false});
    defaultMaskShader.enableStencilTest();
    defaultMaskShader.setStencilFunction(stencilFunc.EQUAL);
    defaultMaskShader.setStencilReferenceValue(stencilValue.DEFAULT_BORDER);
    const defaultMaskScreenPlane = objFactory.createDrawable(defaultMaskShader);
    defaultMaskScreenPlane.addGeometryChunk(screenChunk);
    defaultMaskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    defaultMaskScene.add(defaultMaskScreenPlane);

    const hoveredMaskShader = new ScreenShader({transparent : true, depthTest: false});
    hoveredMaskShader.enableStencilTest();
    hoveredMaskShader.setStencilFunction(stencilFunc.EQUAL);
    hoveredMaskShader.setStencilReferenceValue(stencilValue.HOVERED_BORDER);
    const hoveredMaskScreenPlane = objFactory.createDrawable(hoveredMaskShader);
    hoveredMaskScreenPlane.addGeometryChunk(screenChunk);
    hoveredMaskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    hoveredMaskScene.add(hoveredMaskScreenPlane);

    const activeMaskShader = new ScreenShader({transparent : true, depthTest: false});
    activeMaskShader.enableStencilTest();
    activeMaskShader.setStencilFunction(stencilFunc.EQUAL);
    activeMaskShader.setStencilReferenceValue(stencilValue.ACTIVE_BORDER);
    const activeMaskScreenPlane = objFactory.createDrawable(activeMaskShader);
    activeMaskScreenPlane.addGeometryChunk(screenChunk);
    activeMaskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    activeMaskScene.add(activeMaskScreenPlane);

    const defaultWidth = BORDER_DEFAULT_LINE_WIDTH * screenUtils.getPixelRatio();
    const defaultOutlineShader = new OutlineShader({transparent : true,
      width: defaultWidth,
      depthTest: false,
      outlineOnly: true});

    const defaultOutlineScreenPlane = objFactory.createDrawable(defaultOutlineShader);
    defaultOutlineScreenPlane.addGeometryChunk(screenChunk);
    defaultOutlineScreenPlane.setDiffuseColor(...BORDER_COLOR);
    defaultOutlineScreenPlane.setOpacity(BORDER_DEFAULT_TRANSPARENCY);
    outlineScene.add(defaultOutlineScreenPlane);

    const hoveredOutlineShader = new OutlineShader({transparent : true,
      width: defaultWidth,
      depthTest: false,
      outlineOnly: true});
    const hoveredOutlineScreenPlane = objFactory.createDrawable(hoveredOutlineShader);
    hoveredOutlineScreenPlane.addGeometryChunk(screenChunk);
    hoveredOutlineScreenPlane.setDiffuseColor(...BORDER_COLOR);
    hoveredOutlineScreenPlane.setOpacity(BORDER_SELECTED_TRANSPARENCY);
    outlineScene.add(hoveredOutlineScreenPlane);

    const activeWidth = BORDER_ACTIVE_LINE_WIDTH * screenUtils.getPixelRatio();
    const activeOutlineShader = new OutlineShader({transparent : true,
      width: activeWidth,
      depthTest: false,
      outlineOnly: true});

    const activeOutlineScreenPlane = objFactory.createDrawable(activeOutlineShader);
    activeOutlineScreenPlane.addGeometryChunk(screenChunk);
    activeOutlineScreenPlane.setDiffuseColor(...BORDER_COLOR);
    activeOutlineScreenPlane.setOpacity(BORDER_SELECTED_TRANSPARENCY);
    outlineScene.add(activeOutlineScreenPlane);
  }

  update() {
    const drawScene = this.getScenes()[0];
    const defaultMaskScene = this.getScenes()[1];
    const hoveredMaskScene = this.getScenes()[2];
    const activeMaskScene = this.getScenes()[3];
    const outlineScene = this.getScenes()[4];

    const width = drawScene.camera.viewport[2];
    const height = drawScene.camera.viewport[3];

    const {drawFB, defaultMaskFB, hoveredMaskFB, activeMaskFB, outlineFB} = this.initFramebuffer(width, height);
    drawScene.setFramebuffer(drawFB);
    defaultMaskScene.setFramebuffer(defaultMaskFB);
    hoveredMaskScene.setFramebuffer(hoveredMaskFB);
    activeMaskScene.setFramebuffer(activeMaskFB);
    outlineScene.setFramebuffer(outlineFB);

    const defaultOutlineScreenPlane = outlineScene.getChildren()[0];
    const defaultMaskTexture = defaultMaskScene.getFramebuffer().colorAttachments[0];
    defaultOutlineScreenPlane.setTexture(defaultMaskTexture, 0);
    const hoveredOutlineScreenPlane = outlineScene.getChildren()[1];
    const hoveredMaskTexture = hoveredMaskScene.getFramebuffer().colorAttachments[0];
    hoveredOutlineScreenPlane.setTexture(hoveredMaskTexture, 0);
    const activeOutlineScreenPlane = outlineScene.getChildren()[2];
    const activeMaskTexture = activeMaskScene.getFramebuffer().colorAttachments[0];
    activeOutlineScreenPlane.setTexture(activeMaskTexture, 0);
  }

  initFramebuffer(width, height) {
    const colorTexture = this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE];
    const depthStencil = this.attachmentsStorage[attachmentId.WORKSPACE_DEPTH_STENCIL];

    const defaultMaskTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});
    const hoveredMaskTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});
    const activeMaskTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});

    const drawFB = new FrameBuffer([colorTexture], depthStencil);
    const defaultMaskFB = new FrameBuffer([defaultMaskTexture], depthStencil);
    const hoveredMaskFB = new FrameBuffer([hoveredMaskTexture], depthStencil);
    const activeMaskFB = new FrameBuffer([activeMaskTexture], depthStencil);
    const outlineFB = new FrameBuffer([colorTexture], null);

    return {drawFB, defaultMaskFB, hoveredMaskFB, activeMaskFB, outlineFB};
  }

}
